<script setup lang="ts">

import {useFormValidation} from "~/composables/validations/useFormValidation";
import Edit from "~/components/icon/UI/Edit.vue";
import UpdateFullName from "~/components/component/Service/order/forms/contact/UpdateFullName.vue";
import ConfirmPhone from "~/components/component/Service/order/forms/contact/ConfirmPhone.vue";
import {useStoreAdditionalPhones} from "~/composables/store/order/mainForm";
import {removePhoneMask} from "~/utils/removePhoneMask";

const {$toast, $toastError} = useNuxtApp()
const {profile} = useProfile()
const isAuth = useStoreAuth()
const changeProfile = ref(false)
const { errors,
  validateName, validateLastName, validatePhone
} = useFormValidation()
const userData = ref({
  first_name: profile.value.first_name,
  last_name: profile.value.last_name,
})
watch(() => profile.value, (value) => {
  userData.value.first_name = value.first_name
  userData.value.last_name = value.last_name
})
const props = defineProps({
  isFilled: Boolean,
  hide: Boolean,
  order: Number
})
const additionalPhones = useStoreAdditionalPhones()
additionalPhones.value = [{number: ''}]
const phoneErrors = ref<Record<number, string>>({})

const emit = defineEmits(['next', 'back'])

function goBack() {
  emit('back', props.order)
}

const handleUpdateProfile = async () => {
  if (userData.value.first_name)
    validateName(userData.value.first_name)
  if (userData.value.last_name)
    validateLastName(userData.value.last_name)

  if (Object.keys(errors.value).length) return
  let data: { first_name?: string; last_name?: string } = {};
  try {
    if (userData.value.first_name) {
      data.first_name = userData.value.first_name
      profile.value.first_name = userData.value.first_name
    }

    if (userData.value.last_name) {
      data.last_name = userData.value.last_name
      profile.value.last_name = userData.value.last_name
    }
    const {response, error} = await updateProfile(data);
    if (response.value) {
      handleNext()
    }
    if (error.value) {
      console.log(error)
      let dataErrors = error.value.data.errors
      errors.value = {
        email: dataErrors.email[0],
      }
    }
  } catch (e) {
    console.log(e)
  }
}
function handleNext() {
  phoneErrors.value = {}
  if (additionalPhones.value.length) {
    additionalPhones.value.forEach((phone, index) => {
      if (!phone?.number) {
        return
      }
      validatePhone(removePhoneMask(phone.number))
      if (errors.value.phone_number) {
        phoneErrors.value[index] = errors.value.phone_number
      }
    })
    if (Object.keys(phoneErrors.value).length) {
      return
    }
  }
  changeProfile.value = false
  emit('next')
}
function handleChangeProfile() {
  goBack()
  changeProfile.value = true
}
</script>

<template>
  <div
      class="order_section flex flex-col gap-2"
      :class="{ 'bg-primary-green bg-opacity-10' : isFilled }"
  >
    <div>
      <div class="flex justify-between items-center gap-6">
        <h2 class="h3">2. {{ $t('order_page.titles.contact_data') }}</h2>
        <UIBtn
            @click="goBack"
            v-if="isFilled"
            border="green"
        >{{ $t('input_forms.button.edit') }}
        </UIBtn>
      </div>
      <div v-if="isAuth && !changeProfile">
        <div class="flex gap-2 mt-3">
          <p  class="md:text-base text-sm font-normal text-secondary-800">
            <span>{{ profile.first_name + ' ' + (profile.last_name ? profile.last_name : '') }}, </span>
            <span>{{ profile.phone_number }}</span>
          </p>
          <Edit @click="handleChangeProfile" class="text-green-700 cursor-pointer"/>
        </div>

        <div v-if="!isFilled" class="flex flex-col gap-3 mt-3">
          <div v-for="(item, index) in additionalPhones">
            <div class="relative max-w-[350px] rounded-[6px] border border-neutral-light-gray overflow-hidden">
              <UIInput
                  v-model="item.number"
                  type="tel"
                  mask="+7(###)###-##-##"
                  :placeholder="$t('input_forms.field.additional_phone.placeholder')"
                  class="rounded-[6px] bg-white pr-[44px]"
                  @input="phoneErrors[index] = ''"
              />
              <div @click="additionalPhones.splice(index, 1)" class="absolute top-0 right-0 w-[44px] h-[44px] flex items-center justify-center cursor-pointer ease-in duration-100 group hover:bg-red-500">
                <IconUIClose class="group-hover:text-white ease-in duration-100"/>
              </div>
            </div>

            <p v-if="phoneErrors[index]" class="text-xs text-red-500 mt-1 pl-2">
              {{ phoneErrors[index] }}
            </p>
          </div>

          <button @click="additionalPhones.push({number: ''})" type="button" class="rounded-full w-fit px-2 py-1 font-medium text-sm bg-secondary-400 flex gap-2 items-center">
            <IconUIPlus class="w-3 h-3"/>
             {{ $t('input_forms.button.add_more') }}
          </button>
        </div>
      </div>
    </div>
    <template v-if="!hide">
      <ConfirmPhone v-show="!isAuth"
      @successEvent="handleNext"
      />
      <template v-if="isAuth && changeProfile">
        <UpdateFullName
            :errors="errors"
            :userData="userData"
            @handleUpdateProfile="handleUpdateProfile"
        />
      </template>
      <UIBtn
          v-if="!isFilled && !changeProfile && isAuth"
          @click="handleNext"
          color="green"
          class="w-fit ml-auto"
      >
        {{ $t('input_forms.button.next') }}
      </UIBtn>
    </template>
  </div>
</template>

<style scoped>

</style>